import { IndexedDBService } from '../../indexed-db/services/db_service';
import { NotificationTypes } from '../services/types/vessel-notifications-services.types';
import { VesselNotificationsModelBase } from './vessel-notifications-model';
import {
    NotificationTypesExtended,
    UnifiedNotification,
    VesselNotificationsEventNames,
    activityLogNotification
} from './types/vessel-notifications.types';
import { VoyageImoModel } from './voyage-imo-model';
import { VoyageImoEventNames } from './voyage-imo-model-impl';
import { VesselAlarmsModel } from './vessel-alarms-model';


export class VesselNotificationsModelImpl extends VesselNotificationsModelBase {
    private _notifications: UnifiedNotification[] = []
    private _syncdbName = 'SyncDatabase'
    private _syncStoreName = 'bg-sync-api-store'

    constructor() {
        super()

        this.loadSyncNotifications();
        this.checkBasicPlanAvailable();

        window.addEventListener('online', () => this.handleOnlineStatus())
        window.addEventListener('offline', () => this.handleOfflineStatus())
        VoyageImoModel!.addEventListener(VoyageImoEventNames.ImoUpdated, () => {
            this._notifications = [];
            this.dispatchEvent(
                new CustomEvent(
                    VesselNotificationsEventNames.NotificationsUpdated,
                    {
                        detail: { notifications: this._notifications },
                        bubbles: true,
                        composed: true,
                    },
                ),
            );
        });

        // Listening for background sync completion
        window.addEventListener('manual-sync-post-data-completed', (event) => {
            console.log('Manual sync completed:', event);
            const evt = event as CustomEvent;
            const { url, success, syncTime } = evt.detail;
            this.updateSyncStatus(url, success ? 'complete' : 'failed');
            localStorage.setItem('lastManualSyncedTime', syncTime);
        });
    }

    get notifications(): UnifiedNotification[] {
        return this._notifications
    }

    getNotificationsByType(type: NotificationTypes): UnifiedNotification[] {
        return this._notifications.filter(
            (notification) => notification.type === type,
        )
    }

    getWeatherAlarm(type: NotificationTypes.HeavyWeather | NotificationTypes.ParametricRoll | NotificationTypes.HeavyRolling | NotificationTypes.HighAccelerationForce): UnifiedNotification | undefined {
        let extendedType: string;
        switch (type) {
            case NotificationTypes.HeavyWeather:
                extendedType = NotificationTypesExtended.HeavyWeather;
                break;
            case NotificationTypes.ParametricRoll:
                extendedType = NotificationTypesExtended.ParametricRoll;
                break;
            case NotificationTypes.HeavyRolling:
                extendedType = NotificationTypesExtended.HeavyRolling;
                break;
            case NotificationTypes.HighAccelerationForce:
                extendedType = NotificationTypesExtended.HighAccelerationForce;
                break;
            default:
                return undefined;
        }

        return this._notifications.find((notification) => notification.type === extendedType);
    }


    updateSyncStatus(
        url: string,
        status: 'submitted' | 'complete' | 'failed',
    ): void {
        let statusChanged = false

        this._notifications = this._notifications.map((notification) => {
            // Only update sync notifications
            if (notification.id === url && notification.type === 'sync') {
                statusChanged = notification.status !== status
                return {
                    ...notification,
                    status,
                    created_at: new Date().toISOString(),
                }
            }
            return notification
        })

        if (statusChanged) {
            this.dispatchEvent(
                new CustomEvent(
                    VesselNotificationsEventNames.NotificationsUpdated,
                    {
                        detail: { notifications: this._notifications },
                        bubbles: true,
                        composed: true,
                    },
                ),
            )
        }
    }

    private handleOnlineStatus() {
        this.loadSyncNotifications();
        this.checkBasicPlanAvailable();
    }

    private handleOfflineStatus() {
        this.loadSyncNotifications();
        this.checkBasicPlanAvailable();
    }

    async loadSyncNotifications(): Promise<void> {
        try {
            const db = await this.openDatabase(this._syncdbName);
            const syncItems = await this.getAllData(db)

            // Get current sync notification IDs
            const currentSyncIds = new Set(
                this._notifications
                    .filter(({ type }) => type === 'sync')
                    .map((n) => n.id),
            )

            // Track which IDs we find in IndexedDB
            const foundIds = new Set<string>()

            for (const item of syncItems) {
                if (this.isPlanUpdateRequest(item.id, item.request?.method)) {
                    foundIds.add(item.id)

                    if (!currentSyncIds.has(item.id)) {
                        // Add new sync notification
                        const syncNotification: activityLogNotification = {
                            id: item.id,
                            url: item.id,
                            requestType: item.request?.method || 'UNKNOWN',
                            type: 'sync',
                            created_at: new Date().toISOString(),
                            status: 'submitted',
                            details: this.extractPlanDetails(
                                item.id,
                                item.request,
                            ),
                        }

                        this._notifications.push(syncNotification)
                    }
                }
            }

            // Remove sync notifications that no longer exist in IndexedDB
            this._notifications = this._notifications.filter((notification) => {
                if (notification.type === 'sync') {
                    return foundIds.has(notification.id)
                }
                // Keep all non-sync notifications
                return true
            })

            this.dispatchEvent(
                new CustomEvent(
                    VesselNotificationsEventNames.NotificationsUpdated,
                    {
                        detail: { notifications: this._notifications },
                        bubbles: true,
                        composed: true,
                    },
                ),
            )
        } catch (error) {
            console.error('Error loading sync notifications:', error)
        }
    }

    // Align with one in offline-utils.ts
    private isPlanUpdateRequest(url: string, method: string): boolean {
        const planUrlPattern = /\/voy-vvc-plansapi\/v1\/plans\/\d+\?.*imo=\d{7}/
        return planUrlPattern.test(url) && method === 'PUT'
    }

    // Extract plan details from the URL and request body
    private extractPlanDetails(url: string, request: any): any {
        try {
            const imoMatch = url.match(/imo=(\d+)/)
            const planMatch = url.match(/plans\/(\d+)/)

            const imo = imoMatch ? imoMatch[1] : null
            const planId = planMatch ? planMatch[1] : null

            let planName = null
            if (request && request.body) {
                try {
                    const body = JSON.parse(request.body)
                    planName = body.name || null
                } catch (e) {
                    console.warn('Could not parse request body', e)
                }
            }

            return {
                imo,
                planId,
                planName,
            }
        } catch (e) {
            console.error('Error extracting plan details', e)
            return {}
        }
    }

    private openDatabase(dbName:string): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(dbName)

            request.onupgradeneeded = (event: any) => {
                const db = event?.target?.result
                if (!db.objectStoreNames.contains(this._syncStoreName)) {
                    db.createObjectStore(this._syncStoreName, { keyPath: 'id' })
                }
            }

            request.onsuccess = (event: any) => {
                resolve(event?.target?.result)
            }

            request.onerror = (event: any) => {
                reject(event?.target?.error)
            }
        })
    }

    private getAllData(db: IDBDatabase): Promise<any[]> {
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this._syncStoreName, 'readonly')
            const store = transaction.objectStore(this._syncStoreName)
            const request = store.getAll()

            request.onsuccess = (event: any) => {
                resolve(event.target.result)
            }

            request.onerror = (event: any) => {
                reject(event.target.error)
            }
        })
    }

    async checkBasicPlanAvailable(currentSchedules: any[] = []): Promise<void> {
        if(this._notifications.some(n => n.type === NotificationTypesExtended.BasicPlan)) {
            // Basic plan notification already exists
            return;
        }
        // Check if there are any schedules with basic plan available
        const schedulesList = currentSchedules ? currentSchedules : await IndexedDBService.schedules.getAll();
        if (schedulesList.length > 0) {
            const scheduleWithBasicPlan = schedulesList.filter((ele) => ele.active && ele.isBasicPlanAvailable);
            if (scheduleWithBasicPlan.length > 0) {
                const basicPlanNotification: UnifiedNotification = {
                    id: 'basic-plan-available',
                    created_at: new Date().toISOString(),
                    type: NotificationTypesExtended.BasicPlan,
                    details: {
                        title: "Continue passage plan via Basic 2.0",
                        message: "Please continue passage plan via Basic 2.0 until your next arrival port.",
                    },
                };

                this._notifications.push(basicPlanNotification);
                this.dispatchEvent(
                    new CustomEvent(
                        VesselNotificationsEventNames.NotificationsUpdated,
                        {
                            detail: { notifications: this._notifications },
                            bubbles: true,
                            composed: true,
                        },
                    ),
                );
            }
        }
    }

    async loadWeatherAlarms(): Promise<void> {
        try {
            this._notifications = this._notifications.filter(
                (n) =>
                    n.type !== NotificationTypesExtended.HeavyWeather &&
                    n.type !== NotificationTypesExtended.ParametricRoll &&
                    n.type !== NotificationTypesExtended.HeavyRolling &&
                    n.type !== NotificationTypesExtended.HighAccelerationForce,
            );

            if (!VesselAlarmsModel) {
                console.warn('VesselAlarmsModel is not available');
                return;
            }

            const [heavyWeatherAlarms, parametricRollAlarms, heavyRollingAlarms, highAccelerationForceAlarms] =
                await Promise.all([
                    VesselAlarmsModel._fetchWeatherAlarms(
                        NotificationTypes.HeavyWeather,
                    ),
                    VesselAlarmsModel._fetchWeatherAlarms(
                        NotificationTypes.ParametricRoll,
                    ),
                    VesselAlarmsModel._fetchWeatherAlarms(
                        NotificationTypes.HeavyRolling,
                    ),
                    VesselAlarmsModel._fetchWeatherAlarms(
                        NotificationTypes.HighAccelerationForce,
                    ),
                ]);
                
            if (heavyWeatherAlarms && heavyWeatherAlarms.length > 0) {
                heavyWeatherAlarms.forEach((alarm) => {
                    const notification: activityLogNotification = {
                        id: `${alarm.id}`,
                        created_at: (alarm as any).created_at || new Date().toISOString(),
                        type: NotificationTypesExtended.HeavyWeather,
                        details: JSON.parse(alarm.data),
                    };
                    this._notifications.push(notification);
                });
            }

            if (parametricRollAlarms && parametricRollAlarms.length > 0) {
                parametricRollAlarms.forEach((alarm) => {
                    const notification: activityLogNotification = {
                        id: `${alarm.id}`,
                        created_at: (alarm as any).created_at || new Date().toISOString(),
                        type: NotificationTypesExtended.ParametricRoll,
                        details: JSON.parse(alarm.data),
                    };
                    this._notifications.push(notification);
                });
            }

            if (heavyRollingAlarms && heavyRollingAlarms.length > 0) {
                heavyRollingAlarms.forEach((alarm) => {
                    const notification: activityLogNotification = {
                        id: `${alarm.id}`,
                        created_at: (alarm as any).created_at || new Date().toISOString(),
                        type: NotificationTypesExtended.HeavyRolling,
                        details: JSON.parse(alarm.data),
                    };
                    this._notifications.push(notification);
                });
            }

            if (highAccelerationForceAlarms && highAccelerationForceAlarms.length > 0) {
                highAccelerationForceAlarms.forEach((alarm) => {
                    const notification: activityLogNotification = {
                        id: `${alarm.id}`,
                        created_at: (alarm as any).created_at || new Date().toISOString(),
                        type: NotificationTypesExtended.HighAccelerationForce,
                        details: JSON.parse(alarm.data),
                    };
                    this._notifications.push(notification);
                });
            }

            this.dispatchEvent(
                new CustomEvent(
                    VesselNotificationsEventNames.NotificationsUpdated,
                    {
                        detail: { notifications: this._notifications },
                        bubbles: true,
                        composed: true,
                    },
                ),
            );
        } catch (error) {
            console.error('[loadWeatherAlarms] Error loading weather alarms:', error);
        }
    }
}
